import { CSSProperties, useCallback, useEffect, useMemo } from "react";

import { SwipeHandler } from "@/components/SwipeHandler";
import { useBoolean } from "@/hooks/useBoolean";
import { useSwipeHandlerName } from "@/hooks/useSwipeHandlerName";
import { SheetProps } from "@/types/framework7-react";

export function useSheetPageReady(pageName: string) {
  const { value: isPageReady, setTrue: readyOn, setFalse: readyOff } = useBoolean(false);

  const { swiperHandlerClassName, swiperHandlerSelector } = useSwipeHandlerName();

  useEffect(() => {
    console.debug("SheetPageReady", pageName, "isPageReady", isPageReady);
  }, [isPageReady, pageName]);

  const getPageProps = useCallback(
    (externalProps?: Partial<SheetProps>): SheetProps => {
      // デフォルトのスタイル
      const defaultStyle: CSSProperties = {
        height: "auto",
        minHeight: "50vh",
        paddingBottom: "var(--f7-safe-area-bottom)",
      };

      return {
        ...externalProps,

        backdrop: true,

        onSheetClose: (...args) => {
          console.debug("SheetPageReady", pageName, "onSheetClose");
          readyOff();
          externalProps?.onSheetClose?.(...args);
        },

        onSheetClosed: (...args) => {
          console.debug("SheetPageReady", pageName, "onSheetClosed");
          externalProps?.onSheetClosed?.(...args);
        },

        onSheetOpen: (...args) => {
          console.debug("SheetPageReady", pageName, "onSheetOpen");
          readyOn();
          externalProps?.onSheetOpen?.(...args);
        },

        onSheetOpened: (...args) => {
          console.debug("SheetPageReady", pageName, "onSheetOpened");
          externalProps?.onSheetOpened?.(...args);
        },

        style: {
          ...defaultStyle,
          ...externalProps?.style,
        },

        swipeHandler: swiperHandlerSelector,

        swipeToClose: true,
      };
    },
    [pageName, readyOff, readyOn, swiperHandlerSelector],
  );

  const swipeHandler = useMemo(() => <SwipeHandler className={swiperHandlerClassName} />, [swiperHandlerClassName]);

  return { getPageProps, isPageReady, swipeHandler };
}
